import React from "react";
import { Control, Controller, UseFormWatch } from "react-hook-form";
import { IonInput, IonRadioGroup, IonLabel, IonItem, IonRadio, IonCheckbox } from "@ionic/react";
import commonFormStyles from "../../components/UI/CommonFormElements/CommonFormElements.module.scss";
import classNames from "classnames/bind";
import styles from "./PollNewUserComponents.module.scss";
import { getFullAddress } from "../../utils";
import { CustomPhoneInput } from "../CustomPhoneInput/CustomPhoneInput";
import { SearchAddressInput, SearchAddressInputTypes } from "../SearchAddressInput/SearchAddressInput";
import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import { FieldOption } from "./pollConfig";
import { SurveyAnswers } from "@core/types";
import i18n from "../../i18n";

const cx = classNames.bind(styles);
const cxCommonForm = classNames.bind(commonFormStyles);

export const renderField = (
  field: FieldOption,
  control: Control<SurveyAnswers>,
  watch: UseFormWatch<SurveyAnswers>,
) => {
  const shouldRenderField = () => {
    if (!field.condition) return true;
    const { field: conditionField, value: conditionValue } = field.condition;
    return watch(conditionField) === conditionValue;
  };

  if (!shouldRenderField()) {
    return null;
  }

  const commonProps = {
    control,
    name: field.name,
    rules: { required: field.required },
  };

  switch (field.type) {
    case "tel":
      return (
        <Controller
          {...commonProps}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <CommonFormElement
                label={i18n.t("Phone number")}
                isValid={!error}
                errorMessage={error?.message}
                isRequired={!!field.required}
                classListItem={cx(!field.required && "opacity-70")}
              >
                <CustomPhoneInput
                  className={cxCommonForm("commonElement", { commonInvalid: !!error })}
                  value={value as string}
                  disabled={!field.required}
                  onChange={(val) => {
                    onChange(val);
                  }}
                />
              </CommonFormElement>
            );
          }}
        />
      );
    case "text":
    case "number":
      return (
        <Controller
          {...commonProps}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <>
              {field.name === "deliveryAddress" ? (
                <SearchAddressInput
                  errorMessage={error?.message}
                  address={value as string}
                  label={field.label}
                  isRequired={!!field.required}
                  invalid={!!error}
                  handleChangeValue={onChange}
                  wrapperItemStyles="w-full !z-5"
                  onOptionSelect={({ address }: SearchAddressInputTypes.SelectedOption) => {
                    address && onChange(getFullAddress(address));
                  }}
                />
              ) : (
                <CommonFormElement
                  isValid={!error}
                  label={field.label}
                  commonLabelClassName="sm:!mb-0"
                  errorMessage={error?.message}
                  isRequired={!!field.required}
                >
                  <IonInput
                    className={cxCommonForm("commonElement", { commonInvalid: !!error })}
                    type={field.type === "text" ? "text" : "number"}
                    value={value as string}
                    onIonChange={(e) => onChange(e.detail.value)}
                  />
                </CommonFormElement>
              )}
            </>
          )}
        />
      );
    case "radio":
      return (
        <Controller
          {...commonProps}
          render={({ field: { onChange, value } }) => (
            <IonRadioGroup value={value} onIonChange={(e) => onChange(e.detail.value)}>
              <IonLabel className="font-bold mb-2 block">
                {field.label} {field.required && <span className="text-[--ion-color-danger]">*</span>}
              </IonLabel>
              {field.options!.map((option: string) => (
                <IonItem key={option} lines="none" className={cx("radio-item")}>
                  <IonRadio className={cx("custom-radio")} value={option} mode="md" />
                  <IonLabel className="ml-2">{option}</IonLabel>
                </IonItem>
              ))}
            </IonRadioGroup>
          )}
        />
      );
    case "checkbox":
      return (
        <Controller
          {...commonProps}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const customOptionLabel = field.customOptionLabel || i18n.t("Your own version");
            const customValue = Array.isArray(value)
              ? value.find((item: string) => !field.options!.includes(item) && item !== customOptionLabel)
              : "";

            const isCustomOptionChecked = value?.includes(customOptionLabel) || !!customValue;

            return (
              <>
                <IonLabel position={"stacked"} className={cxCommonForm("commonLabel")}>
                  <div>
                    {field.label} {field.required && <span className="text-[--ion-color-danger]">*</span>}
                  </div>
                  {field.hint && <span className="ion-text-wrap text-gray-500">{field.hint}</span>}
                </IonLabel>

                {field.options?.map((option: string) => (
                  <IonItem key={option} className="w-full flex items-center gap-4" lines="none">
                    <IonCheckbox
                      slot="start"
                      checked={value?.includes(option)}
                      onIonChange={(e) => {
                        const newValue = e.detail.checked
                          ? [...(value || []), option]
                          : (Array.isArray(value) ? value : []).filter((item: string) => item !== option);
                        onChange(newValue);
                      }}
                    />
                    <IonLabel>{option}</IonLabel>
                  </IonItem>
                ))}

                <IonItem className="w-full flex items-center gap-4" lines="none">
                  {field.hasCustomOption && (
                    <>
                      <IonCheckbox
                        slot="start"
                        checked={isCustomOptionChecked}
                        onIonChange={(e) => {
                          const newValue = e.detail.checked
                            ? [
                                ...(Array.isArray(value) ? value : []).filter((item: string) =>
                                  field.options!.includes(item),
                                ),
                                customOptionLabel,
                                customValue,
                              ].filter(Boolean)
                            : (Array.isArray(value) ? value : []).filter(
                                (item: string) => field.options?.includes(item) && item !== customOptionLabel,
                              );
                          onChange(newValue);
                        }}
                      />
                      <IonLabel>{customOptionLabel}</IonLabel>
                      <IonInput
                        type="text"
                        placeholder={i18n.t("Enter your own version")}
                        className={cx("ion-no-padding ml-8 border-b-1 !pl-0")}
                        value={customValue}
                        onIonChange={(e) => {
                          const newCustomValue = e.detail.value || "";
                          const newValue = [
                            ...(Array.isArray(value) ? value : []).filter(
                              (item: string) => field.options?.includes(item) || item === customOptionLabel,
                            ),
                            newCustomValue,
                          ].filter(Boolean);
                          onChange(newValue);
                        }}
                      />
                    </>
                  )}
                </IonItem>

                {error && <div className="text-[--ion-color-danger] mt-1">{error.message}</div>}
              </>
            );
          }}
        />
      );
    default:
      return null;
  }
};
